import {Observable, ReplaySubject, throwError} from "rxjs";
import {EventEmitter, Injectable, Output} from "@angular/core";
import {HttpEvent, HttpHandler, HttpRequest} from "@angular/common/http";
import {catchError, finalize, switchMap} from 'rxjs/operators';
import {ErrorService} from "./error.service";
import {AppService} from "./app.service";

@Injectable({
  providedIn: 'root'
})
export class RequestQueueService {
  @Output('queueChanged') public readonly queueChangedE: EventEmitter<any> = new EventEmitter();
  @Output('processedChanged') public readonly processedChangedE: EventEmitter<any> = new EventEmitter();

  private _queueList: Array<string> = ['/reports/generate'];
  private _max: number = 20;
  private _totalQueue: number = 0;
  private _totalProcessed: number = 0;
  private _inProgress: number = 0;
  private _queue: ReplaySubject<any>[] = [];

  constructor(
    private readonly errorS: ErrorService,
    private readonly appS: AppService
  ) {}

  public reset(): void {
    this._totalQueue = 0;
    this._totalProcessed = 0;
    this._inProgress = 0;
    this._queue = [];
  }

  public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    request = request.clone({
      withCredentials: true,
      url: (request.url + (this.appS.authToken !== undefined ? (request.urlWithParams.split('?').length > 1 ? '&' : '?') + 'auth_token=' + atob(this.appS.authToken) : ''))
    });

    const requestQueueItem$: ReplaySubject<any> = new ReplaySubject<any>();
    const requestHandle$: Observable<any> = next.handle(request)
      .pipe(
        catchError((err) => {
          if (err.status === 401) {
            this.appS.redirect401();
          } else if (err.status === 402) {
            this.errorS.onHttpInterceptorError$.next(err);
            this.appS.redirect402();
          } else {
            this.errorS.onHttpInterceptorError$.next(err);
          }

          return throwError(err);
        })
      );

    const result$ = requestQueueItem$.pipe(
      switchMap(() => requestHandle$.pipe(
        finalize(() => {
          if (this._inProgress) {
            this._inProgress--;
          }

          this._totalProcessed += 1;
          this.processedChangedE.emit(this._totalProcessed);
          this._clean_queue();
          this._processNextRequest();
        }),
        catchError(err => {
          if (this._inProgress) {
            this._inProgress--;
          }

          this._clean_queue();
          this._processNextRequest();
          return throwError(err);
        })
      ))
    );

    if (this._canPutInTheQueue(request.url)) {
      this._queue.push(requestQueueItem$);

      this._totalQueue += 1;
      this.queueChangedE.emit(this._totalQueue);

      if (this._queue.length && this._inProgress < this._max) {
        this._processNextRequest();
      }

      return result$;
    }
    return requestHandle$;
  }

  private _processNextRequest(): void {
    for (let i: number = this._inProgress; i < this._max; i++) {
      this._dispatchRequest();
    }
  }

  private _clean_queue(): void {
    this._queue = this._queue.filter((sub: ReplaySubject<any>) => sub.observers.length);
  }

  private _dispatchRequest(): void {
    if (this._queue.length > 0) {
      const nextSub$ = this._queue[0];

      this._queue.shift();
      nextSub$.next(null);
      nextSub$.complete();
      this._inProgress++;
    } else {
      this._totalQueue = 0;
      this.queueChangedE.emit(this._totalQueue);
      this._totalProcessed = 0;
      this.processedChangedE.emit(this._totalProcessed);
    }
  }

  private _canPutInTheQueue(url: string): boolean {
    for (const str of this._queueList) {
      if (url.includes(str)) {
        return true;
      }
    }

    return false;
  }

}
